import { SchedulerData } from "@/Components/ClientCoachInfo/Scheduler";
import { Appointment } from "@/types/scheduling/appointment";
import {
    addHours,
    addMinutes,
    addMonths,
    addWeeks,
    areIntervalsOverlapping,
    differenceInMinutes,
    eachDayOfInterval,
    endOfMonth,
    isEqual,
    max,
    nextSaturday,
    previousSunday,
    startOfHour,
    startOfMonth,
} from "date-fns";
import {
    formatInTimeZone,
    fromZonedTime,
    getTimezoneOffset,
    toZonedTime,
} from "date-fns-tz";

export type DateTimePickerSchedulerData = Pick<
    SchedulerData,
    | "availabilityRules"
    | "blockedTimes"
    | "coachTimezone"
    | "coachScheduler"
    | "pauseResumesAt"
    | "shouldDoRecurringSchedulingFlow"
>;

export type SelectedTime = {
    window: {
        start: string;
        end: string;
    };
    isRecurring: boolean;
};

export type AvailableTimesByDay = Record<string, Date[]>;

export type DayBlocker = {
    id: number | null;
    start: Date;
    end: Date;
    name: string | null;
    source: DateTimePickerSchedulerData["blockedTimes"][number]["source"];
};

export type BlockedTimesByDay = Record<string, DayBlocker[]>;

type DailyAvailability = Record<number, { startMin: number; endMin: number }[]>;

type NumericBlockedTime = { start: number; end: number };

export type AvailabilityOption = (typeof AVAILABILITY_OPTIONS)[number];

export const AVAILABILITY_OPTIONS = [
    {
        label: "Available",
        key: "available",
        tooltip:
            "Times that you're available on Strawberry and your external calendars",
    },
    {
        label: "Working hours",
        key: "working_hours",
        tooltip: "All times within your working hours",
    },
    {
        label: "All hours",
        key: "all_hours",
        tooltip: "All hours regardless of availability",
    },
] as const;

export const DAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

export const MAX_WEEKS_AHEAD_TO_SCHEDULE = 12;

export const SESSION_DURATION_MINUTES = 50;

const RECURRING_LOOKAHEAD_WEEKS = 2;

export function dayKey(date: Date, userTimezone: string): string {
    return formatInTimeZone(date, userTimezone, "yyyy-MM-dd");
}

export function startOfMonthInTimezone(date: Date, timezone: string): Date {
    return fromZonedTime(startOfMonth(toZonedTime(date, timezone)), timezone);
}

export function endOfMonthInTimezone(date: Date, timezone: string): Date {
    return fromZonedTime(endOfMonth(toZonedTime(date, timezone)), timezone);
}

export function previousSundayInTimezone(date: Date, timezone: string): Date {
    return fromZonedTime(previousSunday(toZonedTime(date, timezone)), timezone);
}

export function nextSaturdayInTimezone(date: Date, timezone: string): Date {
    return fromZonedTime(nextSaturday(toZonedTime(date, timezone)), timezone);
}

export function dayOfWeekInTimezone(date: Date, timezone: string): number {
    return toZonedTime(date, timezone).getDay();
}

export function prettyTimezoneDifference(
    timezone1: string,
    timezone2: string,
): string {
    const difference = differenceInMinutes(
        getTimezoneOffset(timezone1),
        getTimezoneOffset(timezone2),
    );
    if (difference === 0) {
        return "";
    }
    return `(${difference > 0 ? "+" : ""}${difference / 60} hrs)`;
}

export function addMonthsInTimezone(
    date: Date,
    amount: number,
    timezone: string,
): Date {
    return fromZonedTime(
        addMonths(toZonedTime(date, timezone), amount),
        timezone,
    );
}

function generateProposedTimes({
    start,
    end,
    schedulerData,
    availabilityOption,
}: {
    start: Date;
    end: Date;
    schedulerData: DateTimePickerSchedulerData;
    availabilityOption: AvailabilityOption;
}): Date[] {
    const increment =
        schedulerData.coachScheduler.allowHalfHourBooking ||
        availabilityOption.key === "all_hours"
            ? 30
            : 60;
    const minimumNoticeInHours =
        availabilityOption.key === "available"
            ? schedulerData.coachScheduler.minimumNoticeInHours
            : 0;
    const earliestBookable = max([
        addHours(new Date(), minimumNoticeInHours),
        schedulerData.pauseResumesAt ?? new Date(),
    ]);
    const earliestCandidate = max([start, earliestBookable]);
    let currentTime = startOfHour(earliestCandidate);
    while (currentTime < earliestCandidate) {
        currentTime = addMinutes(currentTime, increment);
    }
    const times = [];
    while (currentTime < end) {
        times.push(currentTime);
        currentTime = addMinutes(currentTime, increment);
    }
    return times;
}

function parseTimeStringToMinutes(time: string): number {
    const [hours, minutes] = time.split(":");
    return Number(hours) * 60 + Number(minutes);
}

function buildDailyAvailability(
    availabilityRules: DateTimePickerSchedulerData["availabilityRules"],
): DailyAvailability {
    return availabilityRules.reduce((acc, rule) => {
        const dayNum = DAY_NAMES.indexOf(
            rule.day[0].toUpperCase() + rule.day.slice(1),
        );
        if (!acc[dayNum]) {
            acc[dayNum] = [];
        }
        acc[dayNum].push({
            startMin: parseTimeStringToMinutes(rule.start_time),
            endMin: parseTimeStringToMinutes(rule.end_time),
        });
        return acc;
    }, {} as DailyAvailability);
}

function getBlockedTimesInWindow({
    start,
    end,
    initialBlockedTimes,
}: {
    start: Date;
    end: Date;
    initialBlockedTimes: DateTimePickerSchedulerData["blockedTimes"];
}): DateTimePickerSchedulerData["blockedTimes"] {
    return initialBlockedTimes.filter((blockedTime) =>
        areIntervalsOverlapping(
            { start: blockedTime.start, end: blockedTime.end },
            { start, end },
            { inclusive: true },
        ),
    );
}

function filterBlockedTimes({
    start,
    end,
    initialBlockedTimes,
    reschedulingAppointment,
}: {
    start: Date;
    end: Date;
    initialBlockedTimes: DateTimePickerSchedulerData["blockedTimes"];
    reschedulingAppointment?: Appointment;
}): NumericBlockedTime[] {
    const blockedTimes = getBlockedTimesInWindow({
        start,
        end,
        initialBlockedTimes,
    });

    if (reschedulingAppointment) {
        const reschedulingAppointmentIndex = blockedTimes.findIndex(
            (blockedTime) =>
                isEqual(blockedTime.start, reschedulingAppointment.start_at) &&
                isEqual(blockedTime.end, reschedulingAppointment.end_at),
        );
        if (reschedulingAppointmentIndex !== -1) {
            // Delete the blocked time and add a new blocked slot for the same start time so they can't reschedule to the same time.
            blockedTimes.splice(reschedulingAppointmentIndex, 1, {
                id: null,
                start: reschedulingAppointment.start_at,
                end: reschedulingAppointment.start_at, // This is not a bug
                name: null,
                source: null,
            });
        }
    }

    return blockedTimes.map((blockedTime) => ({
        start: new Date(blockedTime.start).getTime(),
        end: new Date(blockedTime.end).getTime(),
    }));
}

export function eachDayOfIntervalInTimezone(
    start: Date,
    end: Date,
    timezone: string,
): Date[] {
    return eachDayOfInterval({
        start: toZonedTime(start, timezone),
        end: toZonedTime(end, timezone),
    }).map((d) => fromZonedTime(d, timezone));
}

function groupTimesByDay(availableTimes: Date[], userTimezone: string) {
    return availableTimes.reduce((acc, availableTime) => {
        const day = dayKey(availableTime, userTimezone);
        if (!acc[day]) {
            acc[day] = [];
        }
        acc[day].push(availableTime);
        return acc;
    }, {} as AvailableTimesByDay);
}

function isTimeAvailable({
    time,
    dailyAvailability,
    blockedTimes,
    bufferDurationMinutes,
    coachTimezone,
}: {
    time: Date;
    dailyAvailability: DailyAvailability | null;
    blockedTimes: NumericBlockedTime[];
    bufferDurationMinutes: number;
    coachTimezone: string;
}) {
    if (dailyAvailability !== null) {
        /**
         * Calculating toZonedTime for all the proposed times is slow. If we need to speed up this
         * calculation, we can pre-calculate the UTC offset windows for all the time changes in the lookup window.
         * Then we can use the offset windows to calculate the offset much more quickly for each time.
         */
        const coachLocalTime = toZonedTime(time, coachTimezone);
        const availabilityForDay =
            dailyAvailability[coachLocalTime.getDay()] ?? [];
        const minOfDay =
            coachLocalTime.getHours() * 60 + coachLocalTime.getMinutes();
        const apptEndMin = minOfDay + SESSION_DURATION_MINUTES;

        let isWithinCoachAvailability = false;
        for (const rule of availabilityForDay) {
            if (minOfDay >= rule.startMin && apptEndMin <= rule.endMin) {
                isWithinCoachAvailability = true;
                break;
            }
        }

        if (!isWithinCoachAvailability) return false;
    }

    const apptStartMs = time.getTime();
    const apptEndMs = apptStartMs + SESSION_DURATION_MINUTES * 60_000;
    const apptStartWithBufferMs = apptStartMs - bufferDurationMinutes * 60_000;
    const apptEndWithBufferMs = apptEndMs + bufferDurationMinutes * 60_000;

    let isBlocked = false;
    for (const blockedTime of blockedTimes) {
        if (
            apptStartWithBufferMs < blockedTime.end &&
            apptEndWithBufferMs > blockedTime.start
        ) {
            isBlocked = true;
            break;
        }
    }

    return !isBlocked;
}

export function calculateAvailableTimes({
    schedulerData,
    start,
    end,
    recurringIntervalCount,
    reschedulingAppointment,
    isRecurring,
    userTimezone,
    availabilityOption,
}: {
    schedulerData: DateTimePickerSchedulerData;
    start: Date;
    end: Date;
    recurringIntervalCount: number | null;
    reschedulingAppointment?: Appointment;
    isRecurring: boolean;
    userTimezone: string;
    availabilityOption: AvailabilityOption;
}): AvailableTimesByDay {
    const proposedTimes = generateProposedTimes({
        start,
        end,
        schedulerData,
        availabilityOption,
    });

    const dailyAvailability =
        availabilityOption.key === "all_hours"
            ? null
            : buildDailyAvailability(schedulerData.availabilityRules);

    // The recurring lookahead probes past `end`, so blockers out there must survive the filter.
    const blockerWindowEnd =
        isRecurring && recurringIntervalCount !== null
            ? addWeeks(end, RECURRING_LOOKAHEAD_WEEKS * recurringIntervalCount)
            : end;

    const blockedTimes =
        availabilityOption.key === "available"
            ? filterBlockedTimes({
                  start,
                  end: blockerWindowEnd,
                  initialBlockedTimes: schedulerData.blockedTimes,
                  reschedulingAppointment,
              })
            : [];

    let availableTimes = proposedTimes.filter((time) =>
        isTimeAvailable({
            time,
            dailyAvailability,
            blockedTimes,
            bufferDurationMinutes:
                schedulerData.coachScheduler.bufferDurationMinutes,
            coachTimezone: schedulerData.coachTimezone ?? "UTC",
        }),
    );

    if (isRecurring && recurringIntervalCount !== null) {
        availableTimes = availableTimes.filter((time) =>
            [...Array(RECURRING_LOOKAHEAD_WEEKS)].every((_, i) =>
                isTimeAvailable({
                    time: addWeeks(time, (i + 1) * recurringIntervalCount),
                    dailyAvailability,
                    blockedTimes,
                    bufferDurationMinutes:
                        schedulerData.coachScheduler.bufferDurationMinutes,
                    coachTimezone: schedulerData.coachTimezone ?? "UTC",
                }),
            ),
        );
    }

    return groupTimesByDay(availableTimes, userTimezone);
}

export function calculateBlockedTimesByDay({
    schedulerData,
    start,
    end,
    userTimezone,
}: {
    schedulerData: DateTimePickerSchedulerData;
    start: Date;
    end: Date;
    userTimezone: string;
}): BlockedTimesByDay {
    const blockedTimesByDay: BlockedTimesByDay = {};

    for (const blockedTime of getBlockedTimesInWindow({
        start,
        end,
        initialBlockedTimes: schedulerData.blockedTimes,
    })) {
        if (blockedTime.id === null) continue;
        const blocker: DayBlocker = {
            id: blockedTime.id,
            start: new Date(blockedTime.start),
            end: new Date(blockedTime.end),
            name: blockedTime.name,
            source: blockedTime.source,
        };

        for (const dayStart of eachDayOfIntervalInTimezone(
            blocker.start,
            blocker.end,
            userTimezone,
        )) {
            // A blocker ending exactly at midnight does not belong to the following day.
            if (blocker.end <= dayStart) {
                continue;
            }
            const day = dayKey(dayStart, userTimezone);
            if (!blockedTimesByDay[day]) {
                blockedTimesByDay[day] = [];
            }
            blockedTimesByDay[day].push(blocker);
        }
    }

    for (const blockers of Object.values(blockedTimesByDay)) {
        blockers.sort((a, b) => a.start.getTime() - b.start.getTime());
    }

    return blockedTimesByDay;
}

export const BLOCKED_TIMES_CONTAINER_ID = "blocked-times-container";

export function blockerKey(blocker: DayBlocker): string {
    return `${blocker.source}-${blocker.id}`;
}

export function getConflictingBlockers(
    slotStart: Date,
    blockers: DayBlocker[],
): DayBlocker[] {
    const slotStartMs = slotStart.getTime();
    const slotEndMs = slotStartMs + SESSION_DURATION_MINUTES * 60_000;

    return blockers.filter(
        (blocker) =>
            slotStartMs < blocker.end.getTime() &&
            slotEndMs > blocker.start.getTime(),
    );
}
